import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { XProject } from '@jirax/common'

interface DateRange {
  startDate: string
  endDate: string
}

interface EpicFilters {
  selectedProject: XProject | null
  dateRange: DateRange
}

interface UseEpicFiltersOptions {
  projects: XProject[]
  defaultProject?: XProject | null
}

interface UseEpicFiltersReturn {
  filters: EpicFilters
  setSelectedProject: (project: XProject) => void
  setDateRange: (range: DateRange) => void
  isReady: boolean
}

/**
 * Custom hook for managing epic filter state with URL synchronization
 * Provides default values and URL parameter persistence
 */
export function useEpicFilters({
  projects,
  defaultProject
}: UseEpicFiltersOptions): UseEpicFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isReady, setIsReady] = useState(false)

  // Get current year for default date range
  const currentYear = new Date().getFullYear()
  const defaultStartDate = `${currentYear}-01-01`
  const defaultEndDate = `${currentYear}-12-31`

  // Initialize state from URL params or defaults
  const [filters, setFilters] = useState<EpicFilters>(() => {
    const projectKey = searchParams.get('project')
    const startDate = searchParams.get('startDate') || defaultStartDate
    const endDate = searchParams.get('endDate') || defaultEndDate

    // Find project by key from URL or use default
    let selectedProject: XProject | null = null
    if (projectKey && projects.length > 0) {
      selectedProject = projects.find(p => p.key === projectKey) || null
    }

    return {
      selectedProject,
      dateRange: { startDate, endDate }
    }
  })

  // Update state when projects are loaded or URL params change
  useEffect(() => {
    if (projects.length === 0) {
      setIsReady(false)
      return
    }

    const projectKey = searchParams.get('project')
    const startDate = searchParams.get('startDate') || defaultStartDate
    const endDate = searchParams.get('endDate') || defaultEndDate

    let selectedProject: XProject | null = null

    if (projectKey) {
      // Try to find project by key from URL
      selectedProject = projects.find(p => p.key === projectKey) || null
    }

    if (!selectedProject) {
      // Use provided default or first project
      selectedProject = defaultProject || projects[0] || null
    }

    // Validate date range
    const validStartDate = isValidDate(startDate) ? startDate : defaultStartDate
    const validEndDate = isValidDate(endDate) ? endDate : defaultEndDate

    setFilters({
      selectedProject,
      dateRange: {
        startDate: validStartDate,
        endDate: validEndDate
      }
    })

    setIsReady(true)
  }, [projects, searchParams, defaultProject, defaultStartDate, defaultEndDate])

  // Update URL params when filters change
  const updateUrlParams = useCallback((newFilters: EpicFilters) => {
    const newParams = new URLSearchParams(searchParams)

    if (newFilters.selectedProject) {
      newParams.set('project', newFilters.selectedProject.key)
    } else {
      newParams.delete('project')
    }

    if (newFilters.dateRange.startDate !== defaultStartDate) {
      newParams.set('startDate', newFilters.dateRange.startDate)
    } else {
      newParams.delete('startDate')
    }

    if (newFilters.dateRange.endDate !== defaultEndDate) {
      newParams.set('endDate', newFilters.dateRange.endDate)
    } else {
      newParams.delete('endDate')
    }

    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams, defaultStartDate, defaultEndDate])

  const setSelectedProject = useCallback((project: XProject) => {
    const newFilters = { ...filters, selectedProject: project }
    setFilters(newFilters)
    updateUrlParams(newFilters)
  }, [filters, updateUrlParams])

  const setDateRange = useCallback((range: DateRange) => {
    // Validate date range
    const validRange = {
      startDate: isValidDate(range.startDate) ? range.startDate : defaultStartDate,
      endDate: isValidDate(range.endDate) ? range.endDate : defaultEndDate
    }

    // Ensure start date is not after end date
    if (validRange.startDate > validRange.endDate) {
      validRange.endDate = validRange.startDate
    }

    const newFilters = { ...filters, dateRange: validRange }
    setFilters(newFilters)
    updateUrlParams(newFilters)
  }, [filters, updateUrlParams, defaultStartDate, defaultEndDate])

  return {
    filters,
    setSelectedProject,
    setDateRange,
    isReady
  }
}

/**
 * Validates if a string is a valid date in YYYY-MM-DD format
 */
function isValidDate(dateString: string): boolean {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return false
  }

  const date = new Date(dateString)
  return date instanceof Date && !isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === dateString
}